import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * The Cards surface's API client.
 *
 * Mutations all take id lists: a bulk action is one request and one sync, not one per row.
 *
 * **Deliberately does NOT send `X-Client-Id`**, unlike `note.ts`, `search.ts` and `setup.ts`. That
 * header means "this tab has already accounted for this change locally" (`clientId.ts`), and these
 * writes are the case where it is false: deleting, rescheduling or repositioning cards moves the
 * deck counters that `App` holds — the rail's Review badge and Read's `N added · N new` — and
 * nothing here touches them. `Cards` reloads its own rows either way, so the frame costs one extra
 * browse and buys the counters being right. Claiming the id instead left them showing the
 * pre-delete numbers for the rest of the session, since `App` never unmounts to re-ask.
 */

export interface BrowseRow {
  cardId: number;
  noteId: number;
  /** 0-7; Anki's five coloured flags are 1-5. */
  flag: number;
  headword: string;
  reading: string;
  gloss: string;
  tags: string[];
  state: "new" | "learning" | "young" | "mature" | "suspended";
  intervalDays: number;
  lapses: number;
  /** null for new and suspended cards — a new card reports `duePosition` instead. */
  dueDate: string | null;
  duePosition: number | null;
}

export interface BrowseResponse {
  rows: BrowseRow[];
  total: number;
  /** More matched than were hydrated; the list says so rather than implying it has everything. */
  truncated: boolean;
}

export interface DeckSummary {
  name: string;
  cardCount: number;
}

export interface ReviewEntry {
  at: number;
  passed: boolean;
  intervalDays: number;
  tookSeconds: number;
}

export interface CardStats {
  cardId: number;
  reviews: number;
  lapses: number;
  intervalDays: number;
  dueDate: string | null;
  duePosition: number | null;
  history: ReviewEntry[];
}

async function errorMessage(res: Response): Promise<string> {
  bounceIfUnauthorized(res);
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") return body.detail;
    if (typeof body?.error === "string") return body.error;
  } catch {
    // non-JSON body
  }
  return `Request failed (${res.status})`;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

export function browseCards(query: string, limit?: number): Promise<BrowseResponse> {
  const params = new URLSearchParams({ query });
  if (limit) params.set("limit", String(limit));
  return send<BrowseResponse>("GET", `/api/cards?${params}`);
}

export interface DeckList {
  decks: DeckSummary[];
  /**
   * Which deck to land on when none has ever been chosen.
   *
   * The server's answer, because only the server knows the configured vocab deck. It is always one
   * of `decks` when `decks` is non-empty.
   */
  default: string;
}

export function listDecks(): Promise<DeckList> {
  return send<DeckList>("GET", "/api/cards/decks");
}

export function cardStats(cardId: number): Promise<CardStats> {
  return send<CardStats>("GET", `/api/cards/${cardId}/stats`);
}

type Count = { count: number };

/** Anki's own spec: `0` today, `5` in five days, `3-7` a random day in range, `!` also sets ivl. */
export function setDueDate(cardIds: number[], days: string): Promise<Count> {
  return send<Count>("POST", "/api/cards/due-date", { cardIds, days });
}

export function repositionCards(
  cardIds: number[],
  start: number,
  step = 1,
  shiftExisting = true,
): Promise<Count> {
  return send<Count>("POST", "/api/cards/reposition", { cardIds, start, step, shiftExisting });
}

/** The action bar's "Reschedule" is Anki's *forget*: back to new, revlog kept. */
export function rescheduleCards(cardIds: number[], restorePosition = true): Promise<Count> {
  return send<Count>("POST", "/api/cards/reschedule", { cardIds, restorePosition });
}

export function setSuspended(cardIds: number[], suspended: boolean): Promise<Count> {
  return send<Count>("POST", "/api/cards/suspend", { cardIds, suspended });
}

export function setFlag(cardIds: number[], flag: number): Promise<Count> {
  return send<Count>("POST", "/api/cards/flag", { cardIds, flag });
}

export function addTags(noteIds: number[], tags: string): Promise<Count> {
  return send<Count>("POST", "/api/cards/tags/add", { noteIds, tags });
}

export function removeTags(noteIds: number[], tags: string): Promise<Count> {
  return send<Count>("POST", "/api/cards/tags/remove", { noteIds, tags });
}

/** Hard delete, no trash (PRD decision 3). Confirmation-gated at the call site. */
export function deleteNotes(noteIds: number[]): Promise<Count> {
  return send<Count>("POST", "/api/cards/delete", { noteIds });
}
